import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Pagination from '../Pagination/Pagination';
import RangeSlide from '../../components/Elements/RangeSlide/RangeSlide';
import { updateFilter, resetFilter, rebaseFilter, updatePagination } from '../../actions/environment';
import { intersect, unique } from '../../utils/array';
import { translate, prefix } from '../../utils/locale';
import { urlify, upperCaseFirst, formatPrice } from '../../utils/string';
import s from './Filter.scss';

class Filter extends Component {
	constructor(props) {
		super(props);

		this.state = {
			filterOpen: false,
			categories: false,
			colors: false,
			materials: false,
			designers: false,
			collections: false,
			leadTimes: false,
			seatHeights: false,
			shapes: false,
			hash: typeof window !== 'undefined' ? window.location.hash : '',
		};

		this.filterTools = React.createRef();
	}

	componentDidMount() {
		if (this.state.hash) {
			const hashFilter = [];
			this.state.hash.slice(1).split('&').forEach((filterPart) => {
				const filterParts = filterPart.split('=');
				if (filterParts.length === 1) {
					hashFilter.push({ [filterParts[0]]: true });
				} else {
					hashFilter.push({ [filterParts[0]]: filterParts[1].split(',') });
				}
			});

			this.props.updateFilter(hashFilter, true, true, this.props.setup, this.props.currentPath);
			this.setState({ hash: null });
		}
	}

	handleToggleDropdown = (e) => {
		e.preventDefault();
		this.setState((prevState) => ({
			filterOpen: !prevState.filterOpen,
		}));
	}

	handleClearFilter = (e) => {
		e.preventDefault();
		this.props.rebaseFilter(this.props.setup, this.props.currentPath);
		this.props.updatePagination(this.props.currentPath, 1);
	}

	handleToggleGroup = (e) => {
		const $link = e.currentTarget;
		const whichGroup = $link.dataset.group;

		this.setState((prevState) => ({
			categories: false,
			colors: false,
			materials: false,
			designers: false,
			collections: false,
			leadTimes: false,
			seatHeights: false,
			shapes: false,
			[whichGroup]: prevState[whichGroup] ? !prevState[whichGroup] : true,
		}));
	}

	handleFilterUpdate = (e) => {
		e.preventDefault();
		const $link = e.currentTarget;
		const action = $link.getAttribute('href');
		const filterType = action.indexOf('#') === 0 ? action.slice(1, action.indexOf('=')) : 'categories';
		const filterValue = action.indexOf('#') === 0 ? action.slice(action.indexOf('=') + 1) : action.replace(prefix(this.props.setup, ''), '');

		this.props.updateFilter([{ [filterType]: filterValue }], false, false, this.props.setup, this.props.currentPath);
		this.props.updatePagination(this.props.currentPath, 1);

		window.scrollTo(0, 0);
	}

	priceSlideHandler = ([lower, upper]) => {
		this.props.updateFilter([{ price: [lower, upper] }], false, false, this.props.setup, this.props.currentPath);
		this.props.updatePagination(this.props.currentPath, 1);
	}

	render() {
		const { location, data, filtering, materialList, setup, filter, contentSections, isProductPage, currentPath, ...props } = this.props;
		const { filterOpen } = this.state;

		const { filterArrays, filterDescriptions, collectionSwatches, localized, products } = filtering;
		const isLocalized = localized[setup.language];
		const isFiltered = filter.filteredBy.length > 1;

		if (!filter.currentFilterSegment) return null;

		// Category paths are localized.
		const categories = isLocalized ? Object.keys(filterArrays.categories).reduce((acc, category) => {
			const categoryKey = isLocalized.categories[category] ? isLocalized.categories[category].key : category;
			const p = filterArrays?.categoryVariants?.[category] || [];
			return ({ ...acc, [categoryKey]: [...p] });
		}, {}) : filterArrays.categoryVariants;

		// Localize categories
		const categoryTitles = isLocalized ? Object.keys(filterArrays.categories).reduce((acc, category) => {
			const categoryKey = isLocalized.categories[category] ? isLocalized.categories[category].key : category;
			const categoryDescription = isLocalized.categories[category] ? isLocalized.categories[category].description : filterDescriptions.categories[category];
			return ({ ...acc, [categoryKey]: categoryDescription });
		}, {}) : filterDescriptions.categories;

		// Categories that use base path.
		const relevantCategories = filter.currentFilterSegment === 'all' ? { ...categories } : Object.keys(categories).reduce((acc, category) => (category.indexOf(filter.currentFilterSegment) === 0 ? { ...acc, [category]: [...categories[category]] } : acc), {});

		// Localize colors
		const colors = filterArrays.colors;
		const colorDescriptions = filterDescriptions.colors;

		// Localize materials
		const materials = filterArrays.materials;
		const materialDescriptions = materialList.reduce((acc, m) => ({ ...acc, [urlify(translate(setup, m.title))]: translate(setup, m.title) }), {});
		filterDescriptions.materials = materialDescriptions;

		// Localize designers
		const designers = filterArrays.designers;
		const designerDescriptions = filterDescriptions.designers;

		// Localize collections
		const collections = filterArrays.collections;
		const collectionDescriptions = filterDescriptions.collections;

		// Localize leadTimes
		const leadTimes = filterArrays.leadTimes;
		const leadTimeDescriptions = filterDescriptions.leadTimes;

		// Localize seatHeights
		const seatHeights = filterArrays.seatHeights;
		const seatHeightDescriptions = filterDescriptions.seatHeights;

		// Localize shapes
		const shapes = filterArrays.shapes;
		const shapeDescriptions = filterDescriptions.shapes;

		/*
			Filter

			This could benefit from memoization - reselect

			Filters which don't match the base list of products are removed i.e. women men etc.
			Filters which don't match the current categories' list of products are disabled. Except categories.
		*/

		// Get base URI for filtering.
		if (data.baseUri === undefined) return null;

		let relevantBaseProducts = [];
		const path = data.paths[setup.language] || data.paths.default;
		if (data.baseType === 'campaigns') relevantBaseProducts = filterArrays[data.campaignType];
		if (data.baseType === 'categories') relevantBaseProducts = categories[(isProductPage && currentPath) || path];
		if (!relevantBaseProducts) relevantBaseProducts = [];
		relevantBaseProducts = relevantBaseProducts.filter((rbp) => products[rbp]);
		let relevantProducts = [...relevantBaseProducts];

		/*
		const sliceItems = (arr, i) => {
			const rawReturn = parseInt(arr[i], 10);
			return Math.round(rawReturn / 10 ** (String(rawReturn).length - 2)) * 10 ** (String(rawReturn).length - 2);
		};
		*/
		const currentPrices = relevantProducts.map((cacheID) => products?.[cacheID]?.price?.[setup.currency]?.price).sort((a, b) => (parseInt(a, 10) > parseInt(b, 10) ? 1 : -1));
		const priceMedians = [[25, ''], [50, setup.currency === 'USD' ? '$$' : '€€'], [75, '']];

		const filteredProducts = {};

		// Filter only relevant products
		filter.filteredBy.forEach((filterObj) => {
			const filterType = Object.keys(filterObj)[0];
			const filterItem = filterObj[filterType];
			if (filterItem === true || (Array.isArray(filterItem) && filterItem.length > 0)) {
				// Products are filtered
				// Need to update relevantProducts on every pass.
				switch (filterType) {
					case 'categories': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...(categories[type] ? categories[type] : []), ...acc], []));
						filteredProducts.categories = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'collections': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...collections[type], ...acc], []));
						filteredProducts.collections = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'designers': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...designers[type], ...acc], []));
						filteredProducts.designers = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'materials': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...materials[type], ...acc], []));
						filteredProducts.materials = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'colors': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...colors[type], ...acc], []));
						filteredProducts.colors = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'leadTimes': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...leadTimes[type], ...acc], []));
						filteredProducts.leadTimes = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'seatHeights': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...seatHeights[type], ...acc], []));
						filteredProducts.seatHeights = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'shapes': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...shapes[type], ...acc], []));
						filteredProducts.shapes = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'stacks': {
						const concatenatedItems = unique(filterArrays.stacks);
						filteredProducts.stacks = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'outdoor': {
						const concatenatedItems = unique(filterArrays.outdoor);
						filteredProducts.outdoor = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'price': {
						const lowerAmount = parseInt(currentPrices[Math.floor((currentPrices.length - 1) * (parseInt(filterItem[0], 10) / 100))], 10);
						const upperAmount = parseInt(currentPrices[Math.floor((currentPrices.length - 1) * (parseInt(filterItem[1], 10) / 100))], 10);
						const concatenatedItems = relevantProducts.filter((item) => {
							const price = parseInt(products[item].price[setup.currency].price, 10);
							return price >= lowerAmount && price <= upperAmount;
						});
						filteredProducts.price = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					default:
						break;
				}
			}
		});

		// Last filter is special so that the user can select multiple filters.
		let relevantFilterProductsCat = [...relevantBaseProducts];
		let relevantFilterProductsLastCat = [...relevantBaseProducts];
		let lastFilter = '';

		// Restrict filters based on final filter in filteredBy.
		filter.filteredBy.slice(1).forEach((filterObj, i) => {
			lastFilter = Object.keys(filterObj)[0];
			relevantFilterProductsCat = intersect(relevantFilterProductsCat, filteredProducts[lastFilter]);
			if (i < filter.filteredBy.length - 2) relevantFilterProductsLastCat = intersect(relevantFilterProductsLastCat, filteredProducts[lastFilter]);
		});

		let relevantFilterProducts = [...relevantBaseProducts];
		let relevantFilterProductsLast = [...relevantBaseProducts];

		// Restrict filters based on final filter in filteredBy.
		filter.filteredBy.slice(0).forEach((filterObj, i) => {
			lastFilter = Object.keys(filterObj)[0];
			if (lastFilter === 'categories') return;
			relevantFilterProducts = intersect(relevantFilterProducts, filteredProducts[lastFilter]);
			if (i < filter.filteredBy.length - 1) relevantFilterProductsLast = intersect(relevantFilterProductsLast, filteredProducts[lastFilter]);
		});

		/*
			Sorting
		*/
		if (filter.sort) {
			const sortBy = filter.sort.replace(/-(ascending|descending)$/, '');

			relevantProducts = relevantProducts.map((productId) => {
				let sortingValue = filter.sort.indexOf('descending') !== -1 ? 0 : 9999999999;
				if (products[productId].inStock === 0) return { key: productId, sort: sortingValue };

				if (sortBy === 'price') {
					sortingValue = parseInt(products[productId].price.EUR.price, 10);
				} else if (products[productId][sortBy]) {
					sortingValue = products[productId][sortBy];
				}
				return { key: productId, sort: sortingValue };
			}).sort((a, b) => {
				if (a.sort === b.sort) return parseInt(a.key, 10) > parseInt(b.key, 10) ? 1 : -1;
				if (filter.sort.indexOf('descending') === -1) {
					return a.sort > b.sort ? 1 : -1;
				}
				return a.sort < b.sort ? 1 : -1;
			}).map((productObject) => productObject.key);
		} else {
			// Default sorting based on position in categoryVariants list.
			relevantProducts = relevantProducts.sort((a, b) => {
				const posA = relevantBaseProducts.indexOf(a);
				const posB = relevantBaseProducts.indexOf(b);
				return posA > posB ? 1 : -1;
			});
		}

		/*
			Class manipulation
		*/
		const filterToolsClass = [s.filter_tools];
		if (isFiltered) filterToolsClass.push(s.filter_tools__filtered);
		if (relevantBaseProducts.length === 0) filterToolsClass.push(s.filter_tools__empty);

		// Stacks
		const stacksClasses = [s.filtering_section_link, s.filtering_section_link__boolean];
		if (intersect(filterArrays.stacks, (lastFilter === 'stacks' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) stacksClasses.push(s.filtering_section_link__disabled);
		if (filter.stacks) stacksClasses.push(s.filtering_section_link__selected);

		// Outdoor
		const outdoorClasses = [s.filtering_section_link, s.filtering_section_link__boolean];
		if (intersect(filterArrays.outdoor, (lastFilter === 'outdoor' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) outdoorClasses.push(s.filtering_section_link__disabled);
		if (filter.outdoor) outdoorClasses.push(s.filtering_section_link__selected);

		/*
			Filters
		*/
		const currentShapes = currentPath.indexOf('table') !== -1 ? Object.keys(shapes).sort((a, b) => a.localeCompare(b)).map((shape) => {
			if (intersect(shapes[shape], relevantBaseProducts).length === 0) return null;

			const linkClasses = [s.filtering_section_link];
			if (intersect(shapes[shape], (lastFilter === 'shapes' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) linkClasses.push(s.filtering_section_link__disabled);
			if (filter.shapes.indexOf(shape) !== -1) linkClasses.push(s.filtering_section_link__selected);

			return <a key={shape} onClick={this.handleFilterUpdate} className={linkClasses.join(' ')} href={`#shapes=${shape}`}><span>{ upperCaseFirst(shapeDescriptions[shape]) }</span></a>;
		}) : [];

		const currentSeatHeights = Object.keys(seatHeights).sort((a, b) => a.localeCompare(b)).map((seatHeight) => {
			if (intersect(seatHeights[seatHeight], relevantBaseProducts).length === 0) return null;

			const linkClasses = [s.filtering_section_link];
			if (intersect(seatHeights[seatHeight], (lastFilter === 'seatHeights' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) linkClasses.push(s.filtering_section_link__disabled);
			if (filter.seatHeights.indexOf(seatHeight) !== -1) linkClasses.push(s.filtering_section_link__selected);

			return <a key={seatHeight} onClick={this.handleFilterUpdate} className={linkClasses.join(' ')} href={`#seatHeights=${seatHeight}`}><span>{ seatHeightDescriptions[seatHeight] }</span></a>;
		});

		const currentCategories = Object.keys(relevantCategories).sort((a, b) => categoryTitles[a].localeCompare(categoryTitles[b])).map((categoryLevel1) => {
			if (categoryLevel1.split('/').length !== filter.currentFilterSegment.split('/').length + 1) return null;
			if (intersect(relevantCategories[categoryLevel1], relevantBaseProducts).length === 0) return null;

			const linkClasses = [s.filtering_section_link];
			if (filter.categories.indexOf(categoryLevel1) !== -1) linkClasses.push(s.filtering_section_link__selected);

			return <Link key={categoryLevel1} className={linkClasses.join(' ')} to={prefix(setup, categoryLevel1)}><span>{ categoryTitles[categoryLevel1] }</span></Link>;
		});

		const displayingTotalProducts = relevantProducts.filter((cacheID) => products?.[cacheID]?.primary).length;

		return (
			<div className={s.filter}>
				<div className={filterToolsClass.join(' ')} ref={this.filterTools}>
					<div className={[s.filtering, (filterOpen ? s.filtering__open : null)].join(' ')}>
						<button type="button" className={s.filtering_title} aria-expanded={filterOpen} aria-controls="filtering-inner" onClick={this.handleToggleDropdown}>
							{ filterOpen ? translate(setup, 'filterClose') : translate(setup, 'filterOpen') }
							<svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" aria-hidden="true" focusable="false">
								{ filterOpen ? (
									<g fill="none" fillRule="evenodd">
										<line x1="3" x2="20" y1="3" y2="20" stroke="#000" />
										<line x1="20" x2="3" y1="3" y2="20" stroke="#000" />
									</g>
								) : (
									<g fill="none" fillRule="evenodd">
										<line x1="1" x2="22" y1="4.5" y2="4.5" stroke="#000" />
										<line x1="1" x2="22" y1="11.5" y2="11.5" stroke="#000" />
										<line x1="1" x2="22" y1="18.5" y2="18.5" stroke="#000" />
										<line x1="19.5" x2="19.5" y1="1" y2="8" stroke="#000" />
										<line x1="3.5" x2="3.5" y1="8" y2="15" stroke="#000" />
										<line x1="14.5" x2="14.5" y1="15" y2="22" stroke="#000" />
									</g>
								) }
							</svg>
						</button>
						<div id="filtering-inner" className={s.filtering_inner}>
							{
								currentCategories.filter((cSH) => !!cSH).length > 0 && (
									<div className={s.filtering_section}>
										{/* Categories */}
										<fieldset className={[s.filtering_section_group, (this.state.categories ? s.filtering_section_group__open : null)].join(' ')}>
											<legend><button type="button" className={s.filtering_section_group_title} data-group="categories" aria-expanded={this.state.categories} onClick={this.handleToggleGroup}>{ translate(setup, 'filterCategory') }</button></legend>
											<div className={s.filtering_section_group_inner}>
												{ currentCategories }
											</div>
										</fieldset>
									</div>
								)
							}
							<div className={s.filtering_section}>
								{/* Collections */}
								<fieldset className={[s.filtering_section_group, (this.state.collections ? s.filtering_section_group__open : null)].join(' ')}>
									<legend><button type="button" className={s.filtering_section_group_title} data-group="collections" aria-expanded={this.state.collections} onClick={this.handleToggleGroup}>{ translate(setup, 'filterCollection') }</button></legend>
									<div className={s.filtering_section_group_inner}>
										{
											Object.keys(collections).sort((a, b) => a.localeCompare(b)).map((collection) => {
												if (intersect(collections[collection], relevantBaseProducts).length === 0) return null;

												const linkClasses = [s.filtering_section_link];
												if (intersect(collections[collection], (lastFilter === 'collections' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) linkClasses.push(s.filtering_section_link__disabled);
												if (filter.collections.indexOf(collection) !== -1) linkClasses.push(s.filtering_section_link__selected);

												return <a key={collection} onClick={this.handleFilterUpdate} className={linkClasses.join(' ')} href={`#collections=${collection}`}><span>{ collectionDescriptions[collection] }</span></a>;
											})
										}
									</div>
								</fieldset>
							</div>
							<div className={s.filtering_section}>
								{/* Designers */}
								<fieldset className={[s.filtering_section_group, (this.state.designers ? s.filtering_section_group__open : null)].join(' ')}>
									<legend><button type="button" className={s.filtering_section_group_title} data-group="designers" aria-expanded={this.state.designers} onClick={this.handleToggleGroup}>{ translate(setup, 'filterDesigner') }</button></legend>
									<div className={s.filtering_section_group_inner}>
										{
											Object.keys(designers).sort((a, b) => a.localeCompare(b)).map((designer) => {
												if (intersect(designers[designer], relevantBaseProducts).length === 0) return null;

												const linkClasses = [s.filtering_section_link];
												if (intersect(designers[designer], (lastFilter === 'designers' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) linkClasses.push(s.filtering_section_link__disabled);
												if (filter.designers.indexOf(designer) !== -1) linkClasses.push(s.filtering_section_link__selected);

												return <a key={designer} onClick={this.handleFilterUpdate} className={linkClasses.join(' ')} href={`#designers=${designer}`}><span>{ designerDescriptions[designer] }</span></a>;
											})
										}
									</div>
								</fieldset>
							</div>
							<div className={s.filtering_section}>
								{/* Materials */}
								<fieldset className={[s.filtering_section_group, (this.state.materials ? s.filtering_section_group__open : null)].join(' ')}>
									<legend><button type="button" className={s.filtering_section_group_title} data-group="materials" aria-expanded={this.state.materials} onClick={this.handleToggleGroup}>{ translate(setup, 'filterMaterial') }</button></legend>
									<div className={s.filtering_section_group_inner}>
										{
											Object.keys(materials).map((material) => {
												if (intersect(materials[material], relevantBaseProducts).length === 0) return null;

												const linkClasses = [s.filtering_section_link];
												if (intersect(materials[material], (lastFilter === 'materials' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) linkClasses.push(s.filtering_section_link__disabled);
												if (filter.materials.indexOf(material) !== -1) linkClasses.push(s.filtering_section_link__selected);

												return <a key={material} onClick={this.handleFilterUpdate} className={linkClasses.join(' ')} href={`#materials=${material}`}><span>{ materialDescriptions[material] }</span></a>;
											})
										}
									</div>
								</fieldset>
							</div>
							<div className={s.filtering_section}>
								{/* Colors */}
								<fieldset className={[s.filtering_section_group, (this.state.colors ? s.filtering_section_group__open : null)].join(' ')}>
									<legend><button type="button" className={s.filtering_section_group_title} data-group="colors" aria-expanded={this.state.colors} onClick={this.handleToggleGroup}>{ translate(setup, 'filterColor') }</button></legend>
									<div className={s.filtering_section_group_inner}>
										{
											Object.keys(colors).sort((a, b) => (parseInt(colorDescriptions[a].order, 10) > parseInt(colorDescriptions[b].order, 10) ? 1 : -1)).map((color) => {
												if (intersect(colors[color], relevantBaseProducts).length === 0) return null;

												const linkClasses = [s.filtering_section_color];
												if (intersect(colors[color], (lastFilter === 'colors' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) linkClasses.push(s.filtering_section_color__disabled);
												if (filter.colors.indexOf(color) !== -1) linkClasses.push(s.filtering_section_color__selected);

												return <a key={color} onClick={this.handleFilterUpdate} className={linkClasses.join(' ')} href={`#colors=${color}`} aria-label={translate(setup, colorDescriptions[color].title)}><span style={{ backgroundColor: colorDescriptions[color].color }} /></a>;
											})
										}
									</div>
								</fieldset>
							</div>
							{
								currentSeatHeights.filter((cSH) => !!cSH).length > 0 && (
									<div className={s.filtering_section}>
										{/* Seat Height */}
										<fieldset className={[s.filtering_section_group, (this.state.seatHeights ? s.filtering_section_group__open : null)].join(' ')}>
											<legend><button type="button" className={s.filtering_section_group_title} data-group="seatHeights" aria-expanded={this.state.seatHeights} onClick={this.handleToggleGroup}>{ translate(setup, 'filterSeatHeight') }</button></legend>
											<div className={s.filtering_section_group_inner}>
												{ currentSeatHeights }
											</div>
										</fieldset>
									</div>
								)
							}
							{
								currentShapes.filter((cS) => !!cS).length > 0 && (
									<div className={s.filtering_section}>
										{/* Shape */}
										<fieldset className={[s.filtering_section_group, (this.state.shapes ? s.filtering_section_group__open : null)].join(' ')}>
											<legend><button type="button" className={s.filtering_section_group_title} data-group="shapes" aria-expanded={this.state.shapes} onClick={this.handleToggleGroup}>{ translate(setup, 'filterShape') }</button></legend>
											<div className={s.filtering_section_group_inner}>
												{ currentShapes }
											</div>
										</fieldset>
									</div>
								)
							}
							<div className={s.filtering_section}>
								{/* Lead Times */}
								<fieldset className={[s.filtering_section_group, (this.state.leadTimes ? s.filtering_section_group__open : null)].join(' ')}>
									<legend><button type="button" className={s.filtering_section_group_title} data-group="leadTimes" aria-expanded={this.state.leadTimes} onClick={this.handleToggleGroup}>{ translate(setup, 'filterLeadTime') }</button></legend>
									<div className={s.filtering_section_group_inner}>
										{
											Object.keys(leadTimes).sort((a, b) => {
												const aTime = a.replace(/^[^0-9]*(\d+).*$/g, '$1');
												const bTime = b.replace(/^[^0-9]*(\d+).*$/g, '$1');
												return (parseInt(aTime, 10) > parseInt(bTime, 10) ? 1 : -1);
											}).map((leadTime) => {
												if (intersect(leadTimes[leadTime], relevantBaseProducts).length === 0) return null;

												const linkClasses = [s.filtering_section_link];
												if (intersect(leadTimes[leadTime], (lastFilter === 'leadTimes' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) linkClasses.push(s.filtering_section_link__disabled);
												if (filter.leadTimes.indexOf(leadTime) !== -1) linkClasses.push(s.filtering_section_link__selected);

												return <a key={leadTime} onClick={this.handleFilterUpdate} className={linkClasses.join(' ')} href={`#leadTimes=${leadTime}`}><span>{ leadTimeDescriptions[leadTime] }</span></a>;
											})
										}
									</div>
								</fieldset>
							</div>
							<div className={s.filtering_section}>
								{/* Prices */}
								<fieldset className={[s.filtering_section_group, (this.state.prices ? s.filtering_section_group__open : null)].join(' ')}>
									<legend><button type="button" className={s.filtering_section_group_title} data-group="prices" aria-expanded={this.state.prices} onClick={this.handleToggleGroup}>{ translate(setup, 'filterPrice') }</button></legend>
									<div className={[s.filtering_section_group_inner, s.filtering_section_group_inner__prices].join(' ')}>
										<RangeSlide values={currentPrices} labels={[[0, setup.currency === 'USD' ? '$' : '€'], ...priceMedians, [100, setup.currency === 'USD' ? '$$$' : '€€€']]} onChange={this.priceSlideHandler} initialLower={filter.price} />
									</div>
								</fieldset>
							</div>
							{/*
							<a onClick={this.handleFilterUpdate} className={outdoorClasses.join(' ')} href="#outdoor=1"><span>{ translate(setup, 'outdoor') }</span></a>
							<a onClick={this.handleFilterUpdate} className={stacksClasses.join(' ')} href="#stacks=1"><span>{ translate(setup, 'stacks') }</span></a>
							*/}
						</div>
					</div>
				</div>
				{
					relevantProducts.length > 0 ? (
						<div className={s.filter_products}>
							{
								isFiltered && (
									<div className={s.filter_products_filteredBy}>
										<span className={s.filter_products_filteredBy_results}>{ `${displayingTotalProducts} ${displayingTotalProducts <= 1 ? translate(setup, 'resultSingular') : translate(setup, 'resultPlural')}` }</span>
										{
											filter.filteredBy.slice(1).map((fB) => {
												const [filterCategoryValue] = Object.keys(fB);

												if (typeof fB[filterCategoryValue] === 'boolean') {
													return <a key={`selected${filterCategoryValue}`} className={s.filter_products_filteredBy_item} href={`#${filterCategoryValue}=1`} onClick={this.handleFilterUpdate}>{ translate(setup, filterCategoryValue) }</a>;
												}

												if (filterCategoryValue === 'price' && fB[filterCategoryValue].length > 0) {
													return <a key={`selected${filterCategoryValue}`} className={s.filter_products_filteredBy_item} href={`#${filterCategoryValue}=0,100`} onClick={this.handleFilterUpdate}>{ `${formatPrice(setup, currentPrices[Math.floor((currentPrices.length - 1) * (fB[filterCategoryValue][0] / 100))])} - ${formatPrice(setup, currentPrices[Math.floor((currentPrices.length - 1) * (fB[filterCategoryValue][1] / 100))])}` }</a>;
												}

												const filterCategory = filterDescriptions[filterCategoryValue];

												if (filterCategory && fB[filterCategoryValue].length > 0) {
													return fB[filterCategoryValue].map((fCV) => (
														<a key={`selected${filterCategoryValue}${fCV}`} className={s.filter_products_filteredBy_item} href={`#${filterCategoryValue}=${fCV}`} onClick={this.handleFilterUpdate}>{ filterCategory[fCV].title ? translate(setup, filterCategory[fCV].title) : filterCategory[fCV] }</a>
													));
												}

												return null;
											})
										}
										<a className={s.filter_products_filteredBy_clear} href="#clear-filter" onClick={this.handleClearFilter}><span>{ translate(setup, 'filterClear') }</span></a>
									</div>
								)
							}
							<Pagination list={relevantProducts} cache={products} currentPath={prefix(setup, currentPath)} currentHash="" showAll={location.query && location.query.showAll !== undefined} collectionSwatches={collectionSwatches} {...props} />
						</div>
					) : (
						<div className={s.filter_empty}>
							<h1 className={s.filter_empty_title}>{ relevantBaseProducts.length > 0 ? translate(setup, 'filterNoResults') : translate(setup, 'filterNoResultsNoFilter') }</h1>
							<div className={s.filter_empty_links}>
								{
									filter.filteredBy.map((filterObj) => (
										Object.keys(filterObj).map((filterKey) => {
											if (Array.isArray(filterObj[filterKey])) {
												if (filterKey === 'price') {
													return <a className={s.filter_empty_link} href="#price=0,100" onClick={this.handleFilterUpdate}>{ translate(setup, 'filterPrice') }</a>;
												}
												return filterObj[filterKey].map((filterItem) => {
													if (filterKey === 'categories') {
														return <a className={s.filter_empty_link} href={prefix(setup, filterItem)} onClick={this.handleFilterUpdate}>{ categoryTitles[filterItem] }</a>;
													}
													let title = filterDescriptions[filterKey][filterItem];
													if (filterKey === 'outdoor') title = translate(setup, 'outdoor');
													if (filterKey === 'stacks') title = translate(setup, 'stacks');
													return <a className={s.filter_empty_link} href={`#${filterKey}=${filterItem}`} onClick={this.handleFilterUpdate}>{ title }</a>;
												});
											}
											return <a className={s.filter_empty_link} href={`#${filterKey}=1`} onClick={this.handleFilterUpdate}>{ translate(setup, filterKey) }</a>;
										})
									))
								}
							</div>
						</div>
					)
				}
			</div>
		);
	}
}

const mapState = ({ filter, location, environment }, { productUri = false, ...props }) => ({
	filter,
	location,
	materialList: environment.materials,
	productUri,
	...props,
});

export default hot(module)(connect(mapState, { updateFilter, resetFilter, rebaseFilter, updatePagination })(Filter));
